import { useQuery } from '@tanstack/react-query';
import { ListFilter } from 'lucide-react';

import { BarList } from '@/components/Chart/BarList';
import { Combobox, type ComboboxOption } from '@/components/Combobox/Combobox';
import { Skeleton } from '@/components/Skeleton/Skeleton';
import { type ChosenLookbook, type LookbookAttention } from '@/features/reports/api';
import { HEATMAP_COPY } from '@/features/reports/copy';
import { ReportCard, ReportFailure } from '@/features/reports/parts';
import { lookbookAttentionQuery } from '@/features/reports/queries';
import { type HeatmapWeeks } from '@/features/reports/tabs';
import { formatDate } from '@/lib/format';

const COPY = HEATMAP_COPY.attention;

/**
 * Lookbook page attention (ALS-89): one lookbook's pages by how often clients opened them in the
 * window, most viewed first, as the API orders them. Plain counts: a bar's length is its share of
 * the busiest page, and a page nobody opened says so rather than reading as low interest. A
 * request of its own, so picking a lookbook redraws only this card, and the manager is not in it,
 * since page attention is not per client. The lookbook is the reader's choice, else the most
 * viewed (ADR 0105).
 */
export function PageAttentionCard({
    weeks,
    lookbookId,
    onLookbookChange,
}: {
    weeks: HeatmapWeeks;
    lookbookId: number | undefined;
    onLookbookChange: (lookbookId: number) => void;
}) {
    const attention = useQuery(lookbookAttentionQuery({ weeks, lookbookId }));
    const data = attention.data;

    if (attention.isPending) {
        return (
            <Skeleton
                aria-busy="true"
                aria-label="Loading lookbook page attention"
                className="h-[240px] rounded-md"
            />
        );
    }
    if (attention.isError && !data) {
        return <ReportFailure copy={COPY.failed} onRetry={() => void attention.refetch()} />;
    }
    if (!data) {
        return null;
    }

    const book = data.lookbook;
    return (
        <ReportCard
            title={COPY.title}
            subtitle={COPY.subtitle(weeks)}
            /* The picker sits at the right of the header, as in the prototype, and takes a row of
               its own on a phone. A Combobox rather than a Select: 39 books opened in six weeks is
               too many to scroll, so the list can be searched (ALS-89 QA). */
            actionsClassName={book === null ? undefined : 'basis-full sm:basis-auto'}
            actions={
                book === null ? null : (
                    <Combobox
                        aria-label={COPY.picker}
                        icon={ListFilter}
                        value={String(book.lookbookId)}
                        options={options(data)}
                        // Choosing the book already drawn gives null; it stays drawn.
                        onValueChange={(next) => {
                            if (next !== null) onLookbookChange(Number(next));
                        }}
                        searchPlaceholder={COPY.search}
                        emptyText={COPY.noMatch}
                        className="w-full sm:w-[300px]"
                    />
                )
            }
        >
            {book === null ? (
                <p className="text-muted-foreground py-4 text-sm">{COPY.nothingOpened}</p>
            ) : (
                <div className="flex min-w-0 flex-col gap-3">
                    {book.pages.every((page) => page.views === 0) ? (
                        <p className="text-muted-foreground py-4 text-sm">{notOpened(book)}</p>
                    ) : (
                        <BarList
                            items={book.pages.map((page) => ({
                                label: page.label,
                                value: page.views,
                            }))}
                            format={(views) => (views === 0 ? COPY.none : String(views))}
                            emptyNote={notOpened(book)}
                        />
                    )}
                    {book.removedPageViews > 0 ? (
                        <p className="text-muted-foreground text-xs">
                            {COPY.removed(book.removedPageViews)}
                        </p>
                    ) : null}
                </div>
            )}
        </ReportCard>
    );
}

/**
 * Why a lookbook has no bars. Views on pages since removed are not among its pages, so when there
 * are some the lookbook was opened, just not on a page it still has.
 */
function notOpened(book: ChosenLookbook) {
    return book.removedPageViews > 0 ? COPY.notOpenedCurrent(book.name) : COPY.notOpened(book.name);
}

/**
 * The lookbooks opened in the window, most viewed first, and the one drawn when it is not among
 * them: a lookbook from the address that nobody opened in these weeks is still the reader's choice.
 * Each says the day it went live, since several books share a name (Franka, 30 Oct 2026). The
 * closed picker reads only the name and that day, as the prototype does; the list adds the views
 * on a line of their own, so a long name can never cut them (ALS-484).
 */
function options({ lookbooks, lookbook }: LookbookAttention): ComboboxOption[] {
    const offered = lookbooks.map((choice) => ({
        value: String(choice.lookbookId),
        label: COPY.option(choice.name, formatDate(choice.goLive)),
        description: COPY.views(choice.views),
    }));
    if (lookbook && !lookbooks.some((choice) => choice.lookbookId === lookbook.lookbookId)) {
        offered.push({
            value: String(lookbook.lookbookId),
            label: COPY.option(lookbook.name, formatDate(lookbook.goLive)),
            description: COPY.views(0),
        });
    }
    return offered;
}
